import { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useDebounce } from "../utils/debounce";
import { todayISO } from "../utils/chartHelpers";
import { notify } from "../utils/notify";
import Pagination from "./Pagination";
const PAGE_SIZE = 25;
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine,
} from "recharts";
import { supabase } from "../lib/supabase";
import { db } from "../lib/db";
const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
import { logAudit } from "../utils/auditLog";
import { useApp } from "../context/AppContext";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  navy:   "var(--navy)", navy2: "var(--navy-2)", navy3: "var(--navy-3)", navy4: "var(--navy-4)",
  teal:   "#0ea5a0", tealLt: "rgba(14,165,160,0.12)",
  amber:  "#f0a500", amberLt: "rgba(240,165,0,0.15)",
  red:    "#e05252", redLt:  "rgba(224,82,82,0.12)",
  green:  "#22c55e", greenLt: "rgba(34,197,94,0.12)",
  blue:   "#3b82f6", blueLt:  "rgba(59,130,246,0.12)",
  purple: "#a855f7",
  text1:  "var(--text-1)", text2: "var(--text-2)", text3: "var(--text-3)",
  border: "var(--border)", border2: "var(--border-2)",
};

// ─── REFERENCE RANGES ─────────────────────────────────────────────────────────
// Sex-dimorphic fields: lowM/highM/lowF/highF; else low/high
// critLow/critHigh trigger "LL"/"HH" flags
const REF = {
  "WBC":               { low:4.5,   high:11.0,  cL:2.0,   cH:30.0,  u:"K/uL",    d:"4.5–11.0"       },
  "Hemoglobin":        { lM:13.5,   hM:17.5,    lF:12.0,  hF:15.5,  cL:7.0, cH:20.0, u:"g/dL", dM:"13.5–17.5", dF:"12.0–15.5" },
  "Platelets":         { low:150,   high:400,   cL:50,    cH:1000,  u:"K/uL",    d:"150–400"        },
  "Neutrophils":       { low:50,    high:70,    cL:null,  cH:null,  u:"%",       d:"50–70"           },
  "Bands":             { low:0,     high:5,     cL:null,  cH:null,  u:"%",       d:"0–5"             },
  "Sodium":            { low:136,   high:145,   cL:125,   cH:158,   u:"mEq/L",   d:"136–145"        },
  "Potassium":         { low:3.5,   high:5.1,   cL:2.8,   cH:6.5,   u:"mEq/L",   d:"3.5–5.1"       },
  "Creatinine":        { low:0.7,   high:1.3,   cL:null,  cH:6.0,   u:"mg/dL",   d:"0.7–1.3"       },
  "eGFR":              { low:60,    high:999,   cL:15,    cH:null,  u:"mL/min",  d:"≥60"            },
  "Glucose":           { low:70,    high:100,   cL:40,    cH:500,   u:"mg/dL",   d:"70–100"         },
  "Calcium":           { low:8.5,   high:10.5,  cL:7.0,   cH:13.0,  u:"mg/dL",   d:"8.5–10.5"      },
  "Magnesium":         { low:1.7,   high:2.2,   cL:1.0,   cH:null,  u:"mg/dL",   d:"1.7–2.2"       },
  "Phosphorus":        { low:2.5,   high:4.5,   cL:1.0,   cH:null,  u:"mg/dL",   d:"2.5–4.5"       },
  "AST":               { low:10,    high:40,    cL:null,  cH:null,  u:"U/L",     d:"10–40"          },
  "ALT":               { low:7,     high:56,    cL:null,  cH:null,  u:"U/L",     d:"7–56"           },
  "ALP":               { low:44,    high:147,   cL:null,  cH:null,  u:"U/L",     d:"44–147"         },
  "Albumin":           { low:3.5,   high:5.0,   cL:1.5,   cH:null,  u:"g/dL",    d:"3.5–5.0"       },
  "Total Bilirubin":   { low:0.1,   high:1.2,   cL:null,  cH:null,  u:"mg/dL",   d:"0.1–1.2"       },
  "Total Protein":     { low:6.3,   high:8.2,   cL:null,  cH:null,  u:"g/dL",    d:"6.3–8.2"       },
  "PT":                { low:11.0,  high:13.5,  cL:null,  cH:30.0,  u:"sec",     d:"11.0–13.5"     },
  "INR":               { low:0.8,   high:1.2,   cL:null,  cH:4.0,   u:"",        d:"0.8–1.2"       },
  "PTT":               { low:25.0,  high:35.0,  cL:null,  cH:100,   u:"sec",     d:"25–35"          },
  "Fibrinogen":        { low:200,   high:400,   cL:100,   cH:null,  u:"mg/dL",   d:"200–400"       },
  "Anti-Xa":           { low:0.3,   high:0.7,   cL:null,  cH:null,  u:"IU/mL",   d:"0.3–0.7"       },
  "ESR":               { lM:0, hM:15, lF:0, hF:20, cL:null, cH:null, u:"mm/hr",  dM:"0–15", dF:"0–20" },
  "CRP":               { low:0,     high:1.0,   cL:null,  cH:null,  u:"mg/dL",   d:"0–1.0"          },
  "Procalcitonin":     { low:0,     high:0.5,   cL:null,  cH:null,  u:"ng/mL",   d:"0–0.5"          },
  "Ferritin":          { lM:24, hM:336, lF:11, hF:307, cL:null, cH:null, u:"ng/mL", dM:"24–336", dF:"11–307" },
  "HbA1c":             { low:4.0,   high:5.6,   cL:null,  cH:null,  u:"%",       d:"4.0–5.6"        },
  "TSH":               { low:0.4,   high:4.0,   cL:null,  cH:null,  u:"mIU/L",   d:"0.4–4.0"       },
  "Free T4":           { low:0.8,   high:1.8,   cL:null,  cH:null,  u:"ng/dL",   d:"0.8–1.8"       },
  "PTH":               { low:15,    high:65,    cL:null,  cH:null,  u:"pg/mL",   d:"15–65"          },
  "Vitamin D":         { low:30,    high:100,   cL:null,  cH:null,  u:"ng/mL",   d:"30–100"         },
  "Cortisol":          { low:6.2,   high:19.4,  cL:null,  cH:null,  u:"mcg/dL",  d:"6.2–19.4"      },
  "Troponin I":        { low:0,     high:0.04,  cL:null,  cH:0.4,   u:"ng/mL",   d:"0–0.04"        },
  "BNP":               { low:0,     high:100,   cL:null,  cH:null,  u:"pg/mL",   d:"0–100"          },
  "NT-proBNP":         { low:0,     high:125,   cL:null,  cH:null,  u:"pg/mL",   d:"0–125"          },
  "CK":                { low:30,    high:200,   cL:null,  cH:null,  u:"U/L",     d:"30–200"         },
  "CK-MB":             { low:0,     high:6.3,   cL:null,  cH:null,  u:"ng/mL",   d:"0–6.3"         },
  "Total Cholesterol": { low:0,     high:200,   cL:null,  cH:null,  u:"mg/dL",   d:"0–200"          },
  "LDL":               { low:0,     high:130,   cL:null,  cH:null,  u:"mg/dL",   d:"0–130"          },
  "HDL":               { lM:40, hM:999, lF:50, hF:999, cL:null, cH:null, u:"mg/dL", dM:"≥40", dF:"≥50" },
  "Triglycerides":     { low:0,     high:150,   cL:null,  cH:null,  u:"mg/dL",   d:"0–150"          },
  "Uric Acid":         { lM:3.5, hM:7.2, lF:2.6, hF:6.0, cL:null, cH:null, u:"mg/dL", dM:"3.5–7.2", dF:"2.6–6.0" },
  "LDH":               { low:135,   high:225,   cL:null,  cH:null,  u:"U/L",     d:"135–225"        },
};

const getRefDisplay = (t, sex = "M") => {
  const r = REF[t]; if (!r) return "";
  if (sex === "F") return r.dF || r.d || r.dM || "";
  return r.dM || r.d || r.dF || "";
};

const getFlag = (testName, value, sex = "M") => {
  const r = REF[testName];
  if (!r || value === null || value === undefined) return "N";
  const v = parseFloat(value); if (isNaN(v)) return "N";
  const lo = r.lM !== undefined ? (sex === "M" ? r.lM : r.lF) : r.low;
  const hi = r.hM !== undefined ? (sex === "M" ? r.hM : r.hF) : r.high;
  if (r.cH != null && v >= r.cH) return "HH";
  if (r.cL != null && v <= r.cL) return "LL";
  if (hi != null && hi !== 999 && v > hi) return "H";
  if (lo != null && v < lo)               return "L";
  return "N";
};

const FLAG_STYLE = {
  HH: { color:"#ff2222", fw:700, bg:"rgba(255,34,34,0.15)",  label:"↑↑ CRIT" },
  LL: { color:"#3399ff", fw:700, bg:"rgba(51,153,255,0.15)", label:"↓↓ CRIT" },
  H:  { color:C.red,     fw:600, bg:C.redLt,                 label:"H ↑"     },
  L:  { color:C.blue,    fw:600, bg:C.blueLt,                label:"L ↓"     },
  N:  { color:C.green,   fw:500, bg:"transparent",           label:"✓"        },
};

// ─── PANEL → INDIVIDUAL TESTS MAP ─────────────────────────────────────────────
const PANEL_TESTS = {
  "CBC":                   ["WBC","Hemoglobin","Platelets"],
  "CBC with Differential": ["WBC","Hemoglobin","Platelets","Neutrophils","Bands"],
  "BMP":                   ["Sodium","Potassium","Creatinine","eGFR","Glucose"],
  "CMP":                   ["Sodium","Potassium","Creatinine","eGFR","Glucose","AST","ALT","Calcium","Albumin","Total Protein","Total Bilirubin","ALP"],
  "Electrolytes":          ["Sodium","Potassium","Magnesium","Phosphorus"],
  "PT/INR":                ["PT","INR"],
  "PTT":                   ["PTT"],
  "Fibrinogen":            ["Fibrinogen"],
  "Anti-Xa":               ["Anti-Xa"],
  "ESR":                   ["ESR"],
  "CRP":                   ["CRP"],
  "Procalcitonin":         ["Procalcitonin"],
  "Ferritin":              ["Ferritin"],
  "HbA1c":                 ["HbA1c"],
  "TSH":                   ["TSH"],
  "Free T4":               ["Free T4"],
  "PTH":                   ["PTH"],
  "Vitamin D 25-OH":       ["Vitamin D"],
  "Cortisol":              ["Cortisol"],
  "Troponin I":            ["Troponin I"],
  "BNP":                   ["BNP"],
  "NT-proBNP":             ["NT-proBNP"],
  "CK":                    ["CK"],
  "CK-MB":                 ["CK-MB"],
  "Lipid Panel":           ["Total Cholesterol","LDL","HDL","Triglycerides"],
  "Calcium":               ["Calcium"],
  "Phosphorus":            ["Phosphorus"],
  "Magnesium":             ["Magnesium"],
  "Glucose":               ["Glucose"],
  "Creatinine":            ["Creatinine"],
  "eGFR":                  ["eGFR"],
  "Uric Acid":             ["Uric Acid"],
  "LDH":                   ["LDH"],
  "AST":                   ["AST"],
  "ALT":                   ["ALT"],
  "ALP":                   ["ALP"],
  "Albumin":               ["Albumin"],
  "Total Protein":         ["Total Protein"],
  "Total Bilirubin":       ["Total Bilirubin"],
  // Free-text results (cultures, UA, etc.) — empty = text area
  "Blood Culture": [], "Wound Culture": [], "Joint Fluid Culture": [],
  "UA": [], "Urine Culture": [], "Urine Protein/Creatinine Ratio": [],
  "Lyme Titer": [], "ANA": [], "RF": [], "Anti-CCP": [], "Peripheral Smear": [],
  "Reticulocyte Count": [], "Thrombin Time": [],
};

// ─── QUICK PANELS ─────────────────────────────────────────────────────────────
const QUICK_PANELS = [
  { id:"preop",    name:"Pre-op Basic",       tests:["CBC","CMP","PT/INR"],                                  color:C.teal   },
  { id:"cardiac",  name:"Pre-op Cardiac",     tests:["CBC","CMP","PT/INR","BNP","Troponin I"],               color:C.red    },
  { id:"infect",   name:"Infection Workup",   tests:["CBC with Differential","ESR","CRP","Blood Culture"],   color:C.amber  },
  { id:"metabolic",name:"Metabolic",          tests:["CMP","HbA1c","Lipid Panel","TSH"],                     color:C.purple },
  { id:"bone",     name:"Bone Health",        tests:["Calcium","Phosphorus","Vitamin D 25-OH","PTH"],         color:C.green  },
  { id:"anticoag", name:"Anticoagulation",    tests:["PT/INR","PTT","Anti-Xa"],                              color:C.blue   },
];

const TEST_LIBRARY = [
  { cat:"Hematology",   tests:["CBC","CBC with Differential","Reticulocyte Count","Peripheral Smear"] },
  { cat:"Chemistry",    tests:["BMP","CMP","Electrolytes","BUN","Creatinine","eGFR","Glucose","Calcium","Magnesium","Phosphorus","Uric Acid","LDH"] },
  { cat:"Coagulation",  tests:["PT/INR","PTT","Fibrinogen","Anti-Xa","Thrombin Time"] },
  { cat:"Inflammatory", tests:["ESR","CRP","Procalcitonin","Ferritin"] },
  { cat:"Endocrine",    tests:["HbA1c","TSH","Free T4","PTH","Vitamin D 25-OH","Cortisol"] },
  { cat:"Cardiac",      tests:["Troponin I","BNP","NT-proBNP","CK","CK-MB"] },
  { cat:"Lipids",       tests:["Lipid Panel","LDL","HDL","Triglycerides"] },
  { cat:"Liver",        tests:["AST","ALT","ALP","GGT","Total Bilirubin","Albumin","Total Protein"] },
  { cat:"Urinalysis",   tests:["UA","Urine Culture","Urine Protein/Creatinine Ratio"] },
  { cat:"Microbiology", tests:["Blood Culture","Wound Culture","Joint Fluid Culture","Lyme Titer","ANA","RF","Anti-CCP"] },
];

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
const dAgo = (n) => { const d = new Date(); d.setDate(d.getDate()-n); return d.toISOString().split("T")[0]; };
const isoAgo = (n) => new Date(Date.now() - n * 86400000).toISOString();

export const MOCK_LAB_ORDERS = [
  // ── P001 James Hartley (M) ──
  { id:"LO001", patient_id:"P001", ordered_by:"Dr. Walker", order_date:dAgo(14), priority:"routine", status:"resulted",
    panel_name:"Pre-op Basic", facility:"In-house", tests:["CBC","CMP","PT/INR"],
    clinical_indication:"Pre-operative clearance, rotator cuff repair",
    resulted_at:isoAgo(12), critical_values:true,
    results:[
      {test:"WBC",       value:8.2,  units:"K/uL",  refRange:"4.5–11.0",  flag:"N"},
      {test:"Hemoglobin",value:14.5, units:"g/dL",  refRange:"13.5–17.5", flag:"N"},
      {test:"Platelets", value:235,  units:"K/uL",  refRange:"150–400",   flag:"N"},
      {test:"Sodium",    value:139,  units:"mEq/L", refRange:"136–145",   flag:"N"},
      {test:"Potassium", value:4.1,  units:"mEq/L", refRange:"3.5–5.1",  flag:"N"},
      {test:"Creatinine",value:1.1,  units:"mg/dL", refRange:"0.7–1.3",  flag:"N"},
      {test:"Glucose",   value:524,  units:"mg/dL", refRange:"70–100",    flag:"HH"},
      {test:"AST",       value:28,   units:"U/L",   refRange:"10–40",     flag:"N"},
      {test:"ALT",       value:32,   units:"U/L",   refRange:"7–56",      flag:"N"},
      {test:"Calcium",   value:9.4,  units:"mg/dL", refRange:"8.5–10.5", flag:"N"},
      {test:"PT",        value:12.1, units:"sec",   refRange:"11.0–13.5", flag:"N"},
      {test:"INR",       value:1.1,  units:"",      refRange:"0.8–1.2",  flag:"N"},
    ],
    notes:"Endocrinology consult requested for critically elevated glucose.",
  },
  { id:"LO002", patient_id:"P001", ordered_by:"Dr. Walker", order_date:dAgo(7), priority:"routine", status:"resulted",
    panel_name:"Inflammatory Markers", facility:"In-house", tests:["ESR","CRP"],
    clinical_indication:"Post-operative inflammatory monitoring",
    resulted_at:isoAgo(6), critical_values:false,
    results:[
      {test:"ESR", value:42, units:"mm/hr", refRange:"0–15",  flag:"H"},
      {test:"CRP", value:3.8,units:"mg/dL", refRange:"0–1.0", flag:"H"},
    ],
  },
  { id:"LO003", patient_id:"P001", ordered_by:"Dr. Walker", order_date:dAgo(0), priority:"stat", status:"ordered",
    panel_name:"STAT CBC", facility:"In-house", tests:["CBC"],
    clinical_indication:"Post-operative anemia check", resulted_at:null, critical_values:false, results:[],
  },

  // ── P002 Maria Delgado (F) ──
  { id:"LO004", patient_id:"P002", ordered_by:"Dr. Walker", order_date:dAgo(10), priority:"routine", status:"resulted",
    panel_name:"Pre-op Basic", facility:"Quest Diagnostics", tests:["CBC","CMP","PT/INR"],
    clinical_indication:"Pre-operative clearance, distal radius ORIF",
    resulted_at:isoAgo(8), critical_values:true,
    results:[
      {test:"WBC",       value:9.1,  units:"K/uL",  refRange:"4.5–11.0",  flag:"N"},
      {test:"Hemoglobin",value:11.8, units:"g/dL",  refRange:"12.0–15.5", flag:"L"},
      {test:"Platelets", value:189,  units:"K/uL",  refRange:"150–400",   flag:"N"},
      {test:"Sodium",    value:141,  units:"mEq/L", refRange:"136–145",   flag:"N"},
      {test:"Potassium", value:3.8,  units:"mEq/L", refRange:"3.5–5.1",  flag:"N"},
      {test:"Creatinine",value:0.9,  units:"mg/dL", refRange:"0.7–1.3",  flag:"N"},
      {test:"Glucose",   value:95,   units:"mg/dL", refRange:"70–100",   flag:"N"},
      {test:"Calcium",   value:9.2,  units:"mg/dL", refRange:"8.5–10.5", flag:"N"},
      {test:"PT",        value:24.5, units:"sec",   refRange:"11.0–13.5", flag:"H"},
      {test:"INR",       value:4.8,  units:"",      refRange:"0.8–1.2",  flag:"HH"},
    ],
    notes:"Patient on warfarin for prior DVT. INR critically elevated — anticoagulation management required before surgery.",
  },
  { id:"LO005", patient_id:"P002", ordered_by:"Dr. Walker", order_date:dAgo(21), priority:"routine", status:"resulted",
    panel_name:"Bone Health Panel", facility:"Quest Diagnostics", tests:["Calcium","Phosphorus","Vitamin D 25-OH","PTH"],
    clinical_indication:"Osteoporosis screening",
    resulted_at:isoAgo(19), critical_values:false,
    results:[
      {test:"Calcium",   value:9.2, units:"mg/dL", refRange:"8.5–10.5", flag:"N"},
      {test:"Phosphorus",value:3.1, units:"mg/dL", refRange:"2.5–4.5",  flag:"N"},
      {test:"Vitamin D", value:18,  units:"ng/mL", refRange:"30–100",   flag:"L"},
      {test:"PTH",       value:78,  units:"pg/mL", refRange:"15–65",    flag:"H"},
    ],
  },
  { id:"LO006", patient_id:"P002", ordered_by:"Dr. Walker", order_date:dAgo(2), priority:"routine", status:"ordered",
    panel_name:"UA", facility:"In-house", tests:["UA"],
    clinical_indication:"Pre-operative urinalysis", resulted_at:null, critical_values:false, results:[],
  },

  // ── P003 Robert Chen (M) ──
  { id:"LO007", patient_id:"P003", ordered_by:"Dr. Walker", order_date:dAgo(5), priority:"routine", status:"resulted",
    panel_name:"CBC with Differential", facility:"In-house", tests:["CBC with Differential"],
    clinical_indication:"Evaluate leukocytosis, suspected post-op infection",
    resulted_at:isoAgo(4), critical_values:false,
    results:[
      {test:"WBC",         value:13.2, units:"K/uL",  refRange:"4.5–11.0", flag:"H"},
      {test:"Hemoglobin",  value:13.8, units:"g/dL",  refRange:"13.5–17.5",flag:"N"},
      {test:"Platelets",   value:312,  units:"K/uL",  refRange:"150–400",  flag:"N"},
      {test:"Neutrophils", value:82,   units:"%",     refRange:"50–70",    flag:"H"},
      {test:"Bands",       value:8,    units:"%",     refRange:"0–5",      flag:"H"},
    ],
  },
  { id:"LO008", patient_id:"P003", ordered_by:"Dr. Walker", order_date:dAgo(5), priority:"routine", status:"resulted",
    panel_name:"Inflammatory Markers", facility:"In-house", tests:["ESR","CRP"],
    clinical_indication:"Infection / inflammation workup",
    resulted_at:isoAgo(4), critical_values:false,
    results:[
      {test:"ESR", value:45, units:"mm/hr", refRange:"0–15",  flag:"H"},
      {test:"CRP", value:4.2,units:"mg/dL", refRange:"0–1.0", flag:"H"},
    ],
  },
  { id:"LO009", patient_id:"P003", ordered_by:"Dr. Walker", order_date:dAgo(3), priority:"stat", status:"pending",
    panel_name:"Wound Culture", facility:"In-house", tests:["Wound Culture"],
    clinical_indication:"Post-operative wound infection evaluation", resulted_at:null, critical_values:false, results:[],
  },

  // ── P004 Patricia Monroe (F) ──
  { id:"LO010", patient_id:"P004", ordered_by:"Dr. Walker", order_date:dAgo(30), priority:"routine", status:"resulted",
    panel_name:"Metabolic Panel", facility:"LabCorp", tests:["CMP","HbA1c"],
    clinical_indication:"Diabetes management, pre-op metabolic assessment",
    resulted_at:isoAgo(28), critical_values:false,
    results:[
      {test:"HbA1c",     value:8.2,  units:"%",      refRange:"4.0–5.6",  flag:"H"},
      {test:"Glucose",   value:186,  units:"mg/dL",  refRange:"70–100",   flag:"H"},
      {test:"Creatinine",value:1.6,  units:"mg/dL",  refRange:"0.7–1.3",  flag:"H"},
      {test:"eGFR",      value:42,   units:"mL/min", refRange:"≥60",      flag:"L"},
      {test:"Sodium",    value:138,  units:"mEq/L",  refRange:"136–145",  flag:"N"},
      {test:"Potassium", value:4.6,  units:"mEq/L",  refRange:"3.5–5.1", flag:"N"},
      {test:"AST",       value:35,   units:"U/L",    refRange:"10–40",    flag:"N"},
      {test:"ALT",       value:42,   units:"U/L",    refRange:"7–56",     flag:"N"},
    ],
  },
  { id:"LO011", patient_id:"P004", ordered_by:"Dr. Walker", order_date:dAgo(45), priority:"routine", status:"resulted",
    panel_name:"Bone Health Panel", facility:"LabCorp", tests:["Calcium","Phosphorus","Vitamin D 25-OH","PTH"],
    clinical_indication:"Osteoporosis evaluation, knee OA management",
    resulted_at:isoAgo(43), critical_values:false,
    results:[
      {test:"Calcium",   value:7.8, units:"mg/dL", refRange:"8.5–10.5", flag:"L"},
      {test:"Phosphorus",value:2.1, units:"mg/dL", refRange:"2.5–4.5",  flag:"L"},
      {test:"Vitamin D", value:22,  units:"ng/mL", refRange:"30–100",   flag:"L"},
      {test:"PTH",       value:98,  units:"pg/mL", refRange:"15–65",    flag:"H"},
    ],
  },
  { id:"LO012", patient_id:"P004", ordered_by:"Dr. Walker", order_date:dAgo(30), priority:"routine", status:"resulted",
    panel_name:"Lipid Panel", facility:"LabCorp", tests:["Lipid Panel"],
    clinical_indication:"Cardiovascular risk assessment",
    resulted_at:isoAgo(28), critical_values:false,
    results:[
      {test:"Total Cholesterol",value:224, units:"mg/dL", refRange:"0–200",  flag:"H"},
      {test:"LDL",              value:178, units:"mg/dL", refRange:"0–130",  flag:"H"},
      {test:"HDL",              value:42,  units:"mg/dL", refRange:"≥50",    flag:"L"},
      {test:"Triglycerides",    value:245, units:"mg/dL", refRange:"0–150",  flag:"H"},
    ],
  },
  { id:"LO013", patient_id:"P004", ordered_by:"Dr. Walker", order_date:dAgo(1), priority:"routine", status:"ordered",
    panel_name:"Thyroid Function", facility:"LabCorp", tests:["TSH","Free T4"],
    clinical_indication:"Thyroid disease screening", resulted_at:null, critical_values:false, results:[],
  },

  // ── P005 David Okafor (M) ──
  { id:"LO014", patient_id:"P005", ordered_by:"Dr. Walker", order_date:dAgo(7), priority:"routine", status:"resulted",
    panel_name:"Pre-op Cardiac", facility:"Banner Lab", tests:["CBC","CMP","PT/INR","BNP","Troponin I"],
    clinical_indication:"Pre-operative cardiac clearance, wrist surgery",
    resulted_at:isoAgo(5), critical_values:false,
    results:[
      {test:"WBC",       value:7.8,  units:"K/uL",  refRange:"4.5–11.0",  flag:"N"},
      {test:"Hemoglobin",value:15.1, units:"g/dL",  refRange:"13.5–17.5", flag:"N"},
      {test:"Platelets", value:268,  units:"K/uL",  refRange:"150–400",   flag:"N"},
      {test:"Sodium",    value:140,  units:"mEq/L", refRange:"136–145",   flag:"N"},
      {test:"Potassium", value:4.2,  units:"mEq/L", refRange:"3.5–5.1",  flag:"N"},
      {test:"Creatinine",value:1.0,  units:"mg/dL", refRange:"0.7–1.3",  flag:"N"},
      {test:"Glucose",   value:92,   units:"mg/dL", refRange:"70–100",   flag:"N"},
      {test:"Calcium",   value:9.8,  units:"mg/dL", refRange:"8.5–10.5", flag:"N"},
      {test:"PT",        value:11.8, units:"sec",   refRange:"11.0–13.5", flag:"N"},
      {test:"INR",       value:1.0,  units:"",      refRange:"0.8–1.2",  flag:"N"},
      {test:"BNP",       value:285,  units:"pg/mL", refRange:"0–100",    flag:"H"},
      {test:"Troponin I",value:0.02, units:"ng/mL", refRange:"0–0.04",   flag:"N"},
    ],
    notes:"Mildly elevated BNP — cardiology clearance obtained. Troponin negative.",
  },
  { id:"LO015", patient_id:"P005", ordered_by:"Dr. Walker", order_date:dAgo(7), priority:"routine", status:"resulted",
    panel_name:"Coagulation Panel", facility:"Banner Lab", tests:["PT/INR","PTT","Fibrinogen"],
    clinical_indication:"Pre-operative coagulation assessment",
    resulted_at:isoAgo(5), critical_values:false,
    results:[
      {test:"PT",        value:11.8, units:"sec",   refRange:"11.0–13.5", flag:"N"},
      {test:"INR",       value:1.0,  units:"",      refRange:"0.8–1.2",  flag:"N"},
      {test:"PTT",       value:28.5, units:"sec",   refRange:"25–35",    flag:"N"},
      {test:"Fibrinogen",value:380,  units:"mg/dL", refRange:"200–400",  flag:"N"},
    ],
  },
  { id:"LO016", patient_id:"P005", ordered_by:"Dr. Walker", order_date:dAgo(0), priority:"stat", status:"ordered",
    panel_name:"STAT CBC", facility:"In-house", tests:["CBC"],
    clinical_indication:"Post-operative monitoring", resulted_at:null, critical_values:false, results:[],
  },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = iso.includes("T") ? new Date(iso) : new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" });
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" });
};
const daysSince = (iso) => {
  if (!iso) return 0;
  const d = iso.includes("T") ? new Date(iso) : new Date(iso + "T12:00:00");
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};
const getTrendData = (orders, patientId, testName) =>
  orders
    .filter(o => o.patient_id === patientId && o.status === "resulted" && o.results?.length)
    .flatMap(o => o.results.filter(r => r.test === testName).map(r => ({
      date: fmtDate(o.resulted_at || o.order_date),
      value: r.value,
      refHigh: REF[testName]?.high ?? REF[testName]?.hM,
      refLow:  REF[testName]?.low  ?? REF[testName]?.lM,
    })))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

// ─── SUBCOMPONENTS ────────────────────────────────────────────────────────────
function FlagCell({ flag }) {
  if (!flag || flag === "N") return <span style={{ color:C.green, fontSize:13 }}>✓</span>;
  const s = FLAG_STYLE[flag] || FLAG_STYLE.N;
  return (
    <span style={{
      fontSize:11, fontWeight:s.fw, padding:"2px 7px", borderRadius:4,
      color:s.color, background:s.bg,
      animation: (flag==="HH"||flag==="LL") ? "pulse-crit 1.5s infinite" : "none",
    }}>{s.label}</span>
  );
}

function StatusBadge({ status }) {
  const map = {
    ordered:  { label:"Ordered",  c:C.text2, bg:"rgba(255,255,255,0.06)" },
    pending:  { label:"Pending",  c:C.amber, bg:C.amberLt },
    resulted: { label:"Resulted", c:C.green, bg:C.greenLt },
    critical: { label:"Critical", c:"#ff2222", bg:"rgba(255,34,34,0.15)" },
  };
  const s = map[status] || map.ordered;
  return <span style={{ fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:4, color:s.c, background:s.bg }}>{s.label}</span>;
}

function PriorityBadge({ priority }) {
  if (!priority || priority === "routine") return null;
  return <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:4, color:"#fff", background:C.red, marginLeft:4 }}>STAT</span>;
}

function TrendChart({ data, testName, units }) {
  if (!data || data.length < 2) return (
    <div style={{ padding:"20px", textAlign:"center", color:C.text3, fontSize:12 }}>
      Not enough data points to trend — need 2+ results for {testName}.
    </div>
  );
  const refHigh = data[0]?.refHigh;
  const refLow  = data[0]?.refLow;
  return (
    <div style={{ background:C.navy3, border:`1px solid ${C.border}`, borderRadius:8, padding:"16px 8px", marginTop:8 }}>
      <div style={{ fontSize:12, fontWeight:600, color:C.text2, marginBottom:8, paddingLeft:8 }}>
        Trend: {testName} {units ? `(${units})` : ""}
      </div>
      <ResponsiveContainer width="100%" height={140}>
        <LineChart data={data} margin={{ top:4, right:16, left:0, bottom:4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
          <XAxis dataKey="date" tick={{ fontSize:10, fill:C.text3 }} />
          <YAxis tick={{ fontSize:10, fill:C.text3 }} width={40} />
          <Tooltip
            contentStyle={{ background:C.navy2, border:`1px solid ${C.border2}`, borderRadius:6, fontSize:12 }}
            labelStyle={{ color:C.text2 }}
          />
          {refHigh != null && <ReferenceLine y={refHigh} stroke={C.red} strokeDasharray="4 4" label={{ value:"High", position:"right", fontSize:9, fill:C.red }} />}
          {refLow  != null && refLow > 0 && <ReferenceLine y={refLow} stroke={C.blue} strokeDasharray="4 4" label={{ value:"Low", position:"right", fontSize:9, fill:C.blue }} />}
          <Line type="monotone" dataKey="value" stroke={C.teal} strokeWidth={2} dot={{ fill:C.teal, r:4 }} activeDot={{ r:6 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ResultsTable({ order, sex = "M", allOrders, patientId }) {
  const [trendTest, setTrendTest] = useState(null);
  if (!order?.results?.length) return (
    <div style={{ padding:"40px 0", textAlign:"center", color:C.text3 }}>No results available.</div>
  );
  const trendData = trendTest ? getTrendData(allOrders, patientId, trendTest) : [];
  return (
    <div>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:C.navy3 }}>
              {["Test","Result","Units","Reference Range","Flag",""].map(h => (
                <th key={h} style={{ padding:"9px 12px", textAlign:"left", fontSize:10, fontWeight:600, color:C.text2, textTransform:"uppercase", letterSpacing:"0.06em", borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {order.results.map((r, i) => {
              const f = r.flag || "N";
              const isAbn = f !== "N";
              return (
                <tr key={i} style={{ background: (f==="HH"||f==="LL") ? "rgba(255,34,34,0.06)" : isAbn ? "rgba(255,255,255,0.02)" : "transparent", borderBottom:`1px solid ${C.border}` }}>
                  <td style={{ padding:"9px 12px", fontWeight: isAbn ? 600 : 400 }}>{r.test}</td>
                  <td style={{ padding:"9px 12px", fontWeight:600, color: (f==="HH"||f==="LL") ? "#ff2222" : f==="H" ? C.red : f==="L" ? C.blue : C.text1 }}>
                    {r.value}
                  </td>
                  <td style={{ padding:"9px 12px", color:C.text3, fontSize:12 }}>{r.units}</td>
                  <td style={{ padding:"9px 12px", color:C.text3, fontSize:12 }}>{r.refRange}</td>
                  <td style={{ padding:"9px 12px" }}><FlagCell flag={f} /></td>
                  <td style={{ padding:"9px 12px" }}>
                    <button
                      onClick={() => setTrendTest(t => t === r.test ? null : r.test)}
                      style={{ fontSize:10, padding:"3px 8px", borderRadius:4, border:`1px solid ${trendTest===r.test ? C.teal : C.border2}`, background:"transparent", color:trendTest===r.test ? C.teal : C.text3, cursor:"pointer" }}
                    >📈 Trend</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {trendTest && <TrendChart data={trendData} testName={trendTest} units={order.results.find(r=>r.test===trendTest)?.units || ""} />}
    </div>
  );
}

function ResultViewer({ order, allOrders, sex = "M", onClose }) {
  const [linkedNote, setLinkedNote] = useState("");
  const fileRef = useRef();
  const hasCrit = order?.critical_values;

  if (!order) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", color:C.text3 }}>
      <div style={{ fontSize:32, marginBottom:12 }}>🧪</div>
      <div style={{ fontWeight:600, color:C.text2 }}>Select a lab order to view results</div>
    </div>
  );

  return (
    <div style={{ height:"100%", overflowY:"auto" }}>
      {onClose && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
          <div style={{ fontWeight:700, fontSize:16 }}>{order.panel_name}</div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:C.text2, cursor:"pointer", fontSize:18, lineHeight:1 }}>✕</button>
        </div>
      )}

      {hasCrit && (
        <div style={{
          background:"rgba(255,34,34,0.18)", border:"2px solid #ff2222", borderRadius:8,
          padding:"12px 16px", marginBottom:16, display:"flex", alignItems:"center", gap:10,
          animation:"pulse-crit 1.5s infinite",
        }}>
          <span style={{ fontSize:18 }}>🚨</span>
          <div>
            <div style={{ fontWeight:700, color:"#ff4444", fontSize:13 }}>CRITICAL VALUE — Requires immediate physician review</div>
            <div style={{ fontSize:11, color:"#ff8888", marginTop:2 }}>
              {(Array.isArray(order.results) ? order.results : []).filter(r=>r.flag==="HH"||r.flag==="LL").map(r=>`${r.test}: ${r.value} ${r.units}`).join(" · ")}
            </div>
          </div>
        </div>
      )}

      {!onClose && <div style={{ fontWeight:700, fontSize:15, marginBottom:12 }}>{order.panel_name}</div>}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
        {[
          ["Order Date",     fmtDate(order.order_date)],
          ["Resulted",       fmtDateTime(order.resulted_at)],
          ["Ordered By",     order.ordered_by],
          ["Facility",       order.facility],
          ["Tests",          (Array.isArray(order.tests) ? order.tests : []).join(", ")],
          ["Indication",     order.clinical_indication],
        ].map(([k,v]) => (
          <div key={k} style={{ background:C.navy3, borderRadius:6, padding:"8px 12px" }}>
            <div style={{ fontSize:10, color:C.text3, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>{k}</div>
            <div style={{ fontSize:12, color:C.text2, marginTop:2 }}>{v || "—"}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
        <select
          value={linkedNote}
          onChange={e => setLinkedNote(e.target.value)}
          style={{ background:C.navy3, color:linkedNote ? C.text1 : C.text3, border:`1px solid ${C.border2}`, borderRadius:6, padding:"6px 10px", fontSize:12, flex:1, minWidth:160, cursor:"pointer", outline:"none" }}
        >
          <option value="">Link to clinic note…</option>
          <option value="PN1">2026-03-21 — Clinic Visit</option>
          <option value="PN2">2026-03-20 — Post-Op</option>
          <option value="PN3">2026-03-18 — Follow-Up</option>
        </select>
        <button
          onClick={() => fileRef.current?.click()}
          style={{ padding:"6px 12px", borderRadius:6, border:`1px solid ${C.border2}`, background:"transparent", color:C.text2, cursor:"pointer", fontSize:12 }}
        >⬆ Upload Outside Lab</button>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.png" style={{ display:"none" }}
          onChange={() => notify("Outside lab file uploaded (mock).", "info")} />
      </div>

      {order.notes && (
        <div style={{ background:C.navy3, borderRadius:6, padding:"10px 12px", marginBottom:14, fontSize:12, color:C.text2, borderLeft:`3px solid ${C.amber}` }}>
          <span style={{ fontWeight:600, color:C.amber }}>Note: </span>{order.notes}
        </div>
      )}

      <ResultsTable order={order} sex={sex} allOrders={allOrders} patientId={order.patient_id} />
    </div>
  );
}

// ─── ORDER PANEL ──────────────────────────────────────────────────────────────
function OrderPanel({ patient, onOrder, onClose }) {
  const appCtx = useApp();
  const providerName = appCtx?.currentProvider
    ? `${appCtx.currentProvider.first_name} ${appCtx.currentProvider.last_name}`.trim()
    : 'Provider';
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch]     = useState("");
  const [priority, setPriority] = useState("routine");
  const [indication, setIndication] = useState("");
  const [facility, setFacility] = useState("In-house");
  const [favName, setFavName]   = useState("");
  const [saving, setSaving]     = useState(false);
  const [openCats, setOpenCats] = useState(new Set(["Hematology","Coagulation"]));
  const [toast, setToast]       = useState("");

  const toggle = (test) => setSelected(s => { const n=new Set(s); n.has(test)?n.delete(test):n.add(test); return n; });
  const loadPanel = (tests) => setSelected(s => { const n=new Set(s); tests.forEach(t=>n.add(t)); return n; });
  const toggleCat = (cat) => setOpenCats(s => { const n=new Set(s); n.has(cat)?n.delete(cat):n.add(cat); return n; });

  const filteredLib = useMemo(() => {
    if (!search) return TEST_LIBRARY;
    const q = search.toLowerCase();
    return TEST_LIBRARY.map(c => ({ ...c, tests: c.tests.filter(t => t.toLowerCase().includes(q)) })).filter(c => c.tests.length);
  }, [search]);

  const saveFav = async () => {
    if (!favName.trim() || !selected.size) return;
    setSaving(true);
    const payload = { provider: patient?.ordered_by || "Dr. Walker", name: favName.trim(), tests: Array.from(selected) };
    try { await db.from("favorite_labs").insert([payload]); } catch {}
    setSaving(false); setFavName(""); setToast("Favorite saved!");
    setTimeout(() => setToast(""), 2000);
  };

  const handleOrder = async () => {
    if (!selected.size) return;
    setSaving(true);
    const panelName = Array.from(selected).slice(0,2).join(", ") + (selected.size > 2 ? ` +${selected.size-2}` : "");
    const row = {
      patient_id:          patient?.id,
      practice_id:         appCtx?.practiceId,
      ordered_by:          providerName,
      order_date:          todayISO(),
      priority,
      status:              "ordered",
      panel_name:          panelName,
      tests:               JSON.stringify(Array.from(selected)),
      clinical_indication: indication,
      facility,
      results:             JSON.stringify([]),
      critical_values:     false,
    };
    try {
      const { data, error } = await db.from("lab_orders").insert([row]).select().single();
      if (error) throw error;
      logAudit('create', 'lab_order', data.id, patient?.id, { panel: panelName, priority });
      onOrder(data);
    } catch (err) {
      console.error('[labs] order insert failed:', err.message);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:500, display:"flex", justifyContent:"flex-end" }} onClick={onClose}>
      <div style={{ width:500, maxWidth:"95vw", height:"100%", background:C.navy2, borderLeft:`1px solid ${C.border2}`, overflowY:"auto", padding:24, display:"flex", flexDirection:"column", gap:16 }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontWeight:700, fontSize:16 }}>Order Labs</div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:C.text2, cursor:"pointer", fontSize:20, lineHeight:1 }}>✕</button>
        </div>

        {/* Quick panels */}
        <div>
          <div style={{ fontSize:11, fontWeight:600, color:C.text3, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>Quick Panels</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
            {QUICK_PANELS.map(p => (
              <button key={p.id} onClick={() => loadPanel(p.tests)}
                style={{ padding:"8px 12px", borderRadius:6, border:`1px solid ${p.color}30`, background:`${p.color}10`, color:C.text1, cursor:"pointer", textAlign:"left", fontSize:12, fontWeight:500 }}>
                <div style={{ color:p.color, fontWeight:600, fontSize:12 }}>{p.name}</div>
                <div style={{ fontSize:10, color:C.text3, marginTop:2 }}>{p.tests.join(", ")}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Search + library */}
        <div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search tests…"
            style={{ width:"100%", background:C.navy3, color:C.text1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"7px 12px", fontSize:13, outline:"none", boxSizing:"border-box", marginBottom:10 }} />
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {filteredLib.map(cat => (
              <div key={cat.cat}>
                <div onClick={() => toggleCat(cat.cat)}
                  style={{ fontSize:11, fontWeight:600, color:C.text2, textTransform:"uppercase", letterSpacing:"0.06em", cursor:"pointer", padding:"4px 0", display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ color:C.teal }}>{openCats.has(cat.cat) ? "▾" : "▸"}</span>{cat.cat}
                </div>
                {openCats.has(cat.cat) && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, padding:"6px 0 6px 14px" }}>
                    {cat.tests.map(t => (
                      <label key={t} style={{ display:"flex", alignItems:"center", gap:5, cursor:"pointer", padding:"4px 8px", borderRadius:5, background:selected.has(t) ? C.tealLt : "transparent", border:`1px solid ${selected.has(t) ? C.teal : C.border}`, fontSize:12, color:selected.has(t) ? C.teal : C.text2 }}>
                        <input type="checkbox" checked={selected.has(t)} onChange={() => toggle(t)} style={{ accentColor:C.teal, width:12, height:12 }} />
                        {t}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Selected tests */}
        {selected.size > 0 && (
          <div style={{ background:C.navy3, borderRadius:8, padding:12 }}>
            <div style={{ fontSize:11, fontWeight:600, color:C.text2, textTransform:"uppercase", marginBottom:8 }}>Selected ({selected.size})</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
              {Array.from(selected).map(t => (
                <span key={t} onClick={() => toggle(t)} style={{ fontSize:11, padding:"3px 8px", borderRadius:4, background:C.tealLt, color:C.teal, cursor:"pointer", border:`1px solid ${C.teal}40` }}>
                  {t} ✕
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Priority */}
        <div style={{ display:"flex", gap:8 }}>
          {["routine","stat"].map(p => (
            <button key={p} onClick={() => setPriority(p)}
              style={{ flex:1, padding:"8px", borderRadius:6, border:`1px solid ${priority===p ? (p==="stat" ? C.red : C.teal) : C.border}`, background: priority===p ? (p==="stat" ? C.redLt : C.tealLt) : "transparent", color: priority===p ? (p==="stat" ? C.red : C.teal) : C.text2, cursor:"pointer", fontSize:12, fontWeight:600, textTransform:"uppercase" }}>
              {p === "stat" ? "🚨 STAT" : "Routine"}
            </button>
          ))}
        </div>

        {/* Clinical indication */}
        <textarea value={indication} onChange={e=>setIndication(e.target.value)}
          placeholder="Clinical indication…"
          rows={2}
          style={{ background:C.navy3, color:C.text1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"8px 12px", fontSize:13, outline:"none", resize:"none", fontFamily:"inherit" }}
        />

        {/* Facility */}
        <select value={facility} onChange={e=>setFacility(e.target.value)}
          style={{ background:C.navy3, color:C.text1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"8px 12px", fontSize:13, outline:"none", cursor:"pointer" }}>
          {["In-house","Quest Diagnostics","LabCorp","Banner Lab","Outside Facility"].map(f => <option key={f}>{f}</option>)}
        </select>

        {/* Save as favorite */}
        <div style={{ display:"flex", gap:8 }}>
          <input value={favName} onChange={e=>setFavName(e.target.value)} placeholder="Save as favorite: panel name…"
            style={{ flex:1, background:C.navy3, color:C.text1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"7px 12px", fontSize:12, outline:"none" }} />
          <button onClick={saveFav} disabled={!favName.trim() || !selected.size || saving}
            style={{ padding:"7px 14px", borderRadius:6, border:"none", background:favName.trim() && selected.size ? C.teal : C.navy4, color:favName.trim() && selected.size ? "#fff" : C.text3, cursor:favName.trim() && selected.size ? "pointer" : "not-allowed", fontSize:12, fontWeight:600 }}>
            Save
          </button>
        </div>
        {toast && <div style={{ color:C.green, fontSize:12, textAlign:"center" }}>{toast}</div>}

        {/* Order button */}
        <button onClick={handleOrder} disabled={!selected.size}
          style={{ padding:"12px", borderRadius:8, border:"none", background:selected.size ? C.teal : C.navy4, color:selected.size ? "#fff" : C.text3, cursor:selected.size ? "pointer" : "not-allowed", fontSize:14, fontWeight:700 }}>
          Order {selected.size > 0 ? `${selected.size} Test${selected.size > 1 ? "s" : ""}` : "Labs"}
        </button>
      </div>
    </div>,
    document.body
  );
}

// ─── FAVORITES PANEL ──────────────────────────────────────────────────────────
function FavoritesPanel({ onSelect, onClose }) {
  const [favs, setFavs] = useState([]);

  useEffect(() => {
    db.from("favorite_labs").select("id, name, tests, created_at").order("created_at", { ascending:false })
      .then(({ data }) => setFavs(data?.length ? data : MOCK_FAVS));
  }, []);

  const del = async (id) => {
    try { await db.from("favorite_labs").delete().eq("id", id); } catch {}
    setFavs(f => f.filter(x => x.id !== id));
  };

  return createPortal(
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:500, display:"flex", justifyContent:"flex-end" }} onClick={onClose}>
      <div style={{ width:380, maxWidth:"95vw", height:"100%", background:C.navy2, borderLeft:`1px solid ${C.border2}`, overflowY:"auto", padding:24 }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div style={{ fontWeight:700, fontSize:16 }}>Saved Lab Panels</div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:C.text2, cursor:"pointer", fontSize:20 }}>✕</button>
        </div>
        {favs.length === 0 && <div style={{ color:C.text3, textAlign:"center", padding:"40px 0" }}>No saved panels yet.</div>}
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {favs.map(f => (
            <div key={f.id} style={{ background:C.navy3, borderRadius:8, padding:"12px 14px", border:`1px solid ${C.border}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>{f.name}</div>
                  <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>{f.provider} · {Array.isArray(f.tests) ? f.tests.length : f.tests?.length || "?"} tests</div>
                  <div style={{ fontSize:11, color:C.text2, marginTop:4 }}>{Array.isArray(f.tests) ? f.tests.join(", ") : f.tests}</div>
                </div>
                <div style={{ display:"flex", gap:6, flexShrink:0, marginLeft:8 }}>
                  <button onClick={() => { onSelect(Array.isArray(f.tests) ? f.tests : []); onClose(); }}
                    style={{ padding:"5px 12px", borderRadius:5, border:"none", background:C.teal, color:"#fff", cursor:"pointer", fontSize:11, fontWeight:600 }}>
                    Load
                  </button>
                  <button onClick={() => del(f.id)}
                    style={{ padding:"5px 8px", borderRadius:5, border:`1px solid ${C.border2}`, background:"transparent", color:C.red, cursor:"pointer", fontSize:11 }}>
                    ✕
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

const MOCK_FAVS = [
  { id:"MF1", provider:"Dr. Walker", name:"Standard Pre-op",   tests:["CBC","CMP","PT/INR"] },
  { id:"MF2", provider:"Dr. Walker", name:"Infection Workup",  tests:["CBC with Differential","ESR","CRP","Blood Culture"] },
  { id:"MF3", provider:"Dr. Walker", name:"Bone Health",       tests:["Calcium","Phosphorus","Vitamin D 25-OH","PTH"] },
];

// ─── MARK RESULTED MODAL ──────────────────────────────────────────────────────
function MarkResultedModal({ order, sex = "M", onSave, onClose }) {
  const allTests = useMemo(() =>
    order.tests.flatMap(t => (PANEL_TESTS[t] ?? []).length > 0 ? PANEL_TESTS[t] : [t])
  , [order.tests]);
  const isNumeric = (t) => (PANEL_TESTS[t] ?? [t]).length > 0 && REF[t];
  const [vals, setVals]     = useState({});
  const [textVals, setTextVals] = useState({});
  const [isCrit, setIsCrit] = useState(false);
  const [saving, setSaving] = useState(false);

  const setVal = (t, v) => setVals(p => ({ ...p, [t]: v }));
  const setTextVal = (t, v) => setTextVals(p => ({ ...p, [t]: v }));

  const buildResults = () => {
    return allTests.map(t => {
      const v = vals[t];
      const r = REF[t];
      const numVal = v !== undefined && v !== "" ? parseFloat(v) : null;
      const flag = numVal !== null && !isNaN(numVal) ? getFlag(t, numVal, sex) : "N";
      return {
        test:t, value: numVal ?? textVals[t] ?? "",
        units: r?.u || "",
        refRange: getRefDisplay(t, sex),
        flag,
      };
    }).filter(r => r.value !== "" && r.value !== null && r.value !== undefined);
  };

  const hasCritFlag = () => buildResults().some(r => r.flag === "HH" || r.flag === "LL");

  const handleSave = async () => {
    setSaving(true);
    const results = buildResults();
    const critValues = hasCritFlag() || isCrit;
    const updated = { ...order, status:"resulted", resulted_at: new Date().toISOString(), results, critical_values: critValues };
    // UPDATE by id (was a primary-key upsert — the row always exists in this
    // flow, and db-query doesn't support PK-conflict upserts).
    try {
      await db.from("lab_orders")
        .update({ status: "resulted", resulted_at: updated.resulted_at, results: JSON.stringify(results), critical_values: critValues })
        .eq("id", order.id);
    } catch {}
    setSaving(false);
    onSave(updated);
  };

  const freeTextTests = allTests.filter(t => (PANEL_TESTS[t] ?? []).length === 0 && !REF[t]);
  const numericTests  = allTests.filter(t => !freeTextTests.includes(t));

  return createPortal(
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:600 }} onClick={onClose}>
      <div style={{ background:C.navy2, border:`1px solid ${C.border2}`, borderRadius:12, padding:24, width:560, maxWidth:"96vw", maxHeight:"90vh", overflowY:"auto" }}
        onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>Enter Results — {order.panel_name}</div>
        <div style={{ color:C.text2, fontSize:12, marginBottom:20 }}>
          Patient: {order.patient_id} · Ordered: {fmtDate(order.order_date)} · {order.facility}
        </div>

        {/* Numeric tests */}
        {numericTests.length > 0 && (
          <div style={{ display:"grid", gap:8, marginBottom:16 }}>
            {numericTests.map(t => {
              const r = REF[t];
              const v = vals[t];
              const flag = v !== "" && v !== undefined ? getFlag(t, parseFloat(v), sex) : null;
              const fs = flag ? FLAG_STYLE[flag] : null;
              return (
                <div key={t} style={{ display:"grid", gridTemplateColumns:"1fr 100px 80px 120px 60px", gap:8, alignItems:"center" }}>
                  <div style={{ fontSize:13, fontWeight:500 }}>{t}</div>
                  <input type="number" value={v ?? ""} onChange={e=>setVal(t, e.target.value)}
                    placeholder="Value"
                    style={{ background:C.navy3, color:C.text1, border:`1px solid ${C.border2}`, borderRadius:5, padding:"5px 8px", fontSize:12, outline:"none", width:"100%" }} />
                  <div style={{ fontSize:11, color:C.text3 }}>{r?.u}</div>
                  <div style={{ fontSize:11, color:C.text3 }}>{getRefDisplay(t, sex)}</div>
                  <div>{flag && flag !== "N" && <FlagCell flag={flag} />}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Free text tests */}
        {freeTextTests.map(t => (
          <div key={t} style={{ marginBottom:10 }}>
            <div style={{ fontSize:12, fontWeight:500, color:C.text2, marginBottom:4 }}>{t} (free text)</div>
            <textarea value={textVals[t] || ""} onChange={e=>setTextVal(t, e.target.value)}
              rows={3} placeholder="Enter result text…"
              style={{ width:"100%", background:C.navy3, color:C.text1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"8px 10px", fontSize:12, outline:"none", resize:"none", fontFamily:"inherit", boxSizing:"border-box" }} />
          </div>
        ))}

        {/* Critical checkbox */}
        <label style={{ display:"flex", alignItems:"center", gap:8, marginBottom:20, cursor:"pointer", fontSize:13 }}>
          <input type="checkbox" checked={isCrit} onChange={e=>setIsCrit(e.target.checked)} style={{ accentColor:C.red, width:14, height:14 }} />
          <span style={{ color: isCrit ? C.red : C.text2, fontWeight: isCrit ? 600 : 400 }}>
            Mark as containing critical value (requires immediate physician review)
          </span>
        </label>

        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ padding:"9px 20px", borderRadius:6, border:`1px solid ${C.border2}`, background:"transparent", color:C.text2, cursor:"pointer", fontSize:13 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding:"9px 20px", borderRadius:6, border:"none", background:C.teal, color:"#fff", cursor:"pointer", fontSize:13, fontWeight:700 }}>
            {saving ? "Saving…" : "Save Results"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LABS TAB (patient chart) ─────────────────────────────────────────────────
export function LabsTab({ patient }) {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showOrder, setShowOrder]         = useState(false);
  const [showFavs, setShowFavs]           = useState(false);
  const [deleteLabConfirm, setDeleteLabConfirm] = useState(null);
  const [voidModal, setVoidModal]         = useState(null);
  const [voidReason, setVoidReason]       = useState("Specimen labeling error");
  const [voidNote, setVoidNote]           = useState("");

  useEffect(() => {
    if (!patient?.id || !/^[0-9a-f-]{36}$/.test(patient.id)) return;
    db.from("lab_orders").select("id, patient_id, order_date, panel_name, tests, priority, status, resulted_at, results, critical_values, ordered_by, practice_id").eq("patient_id", patient.id).order("order_date", { ascending:false }).limit(100)
      .then(({ data }) => {
        const list = data || [];
        setOrders(list);
        if (list.length) setSelected(list.find(o => o.critical_values) || list[0]);
      });
  }, [patient.id]);

  const handleOrder = (order) => {
    setOrders(o => [order, ...o]);
    setSelected(order);
    setShowOrder(false);
  };

  const handleDeleteLab = async () => {
    if (!deleteLabConfirm) return;
    await db.from("lab_orders").delete().eq("id", deleteLabConfirm.id);
    setOrders(o => o.filter(x => x.id !== deleteLabConfirm.id));
    if (selected?.id === deleteLabConfirm.id) setSelected(null);
    setDeleteLabConfirm(null);
  };

  const handleVoidLab = async () => {
    if (!voidModal) return;
    const reason = voidReason + (voidNote ? " — " + voidNote : "");
    const updated = { ...voidModal, status: "voided", voided_at: new Date().toISOString(), void_reason: reason };
    await db.from("lab_orders").update({ status: "voided", voided_at: updated.voided_at, void_reason: reason }).eq("id", voidModal.id);
    setOrders(o => o.map(x => x.id === voidModal.id ? updated : x));
    if (selected?.id === voidModal.id) setSelected(updated);
    setVoidModal(null);
    setVoidReason("Specimen labeling error");
    setVoidNote("");
  };

  const statusForDisplay = (o) => o.critical_values ? "critical" : o.status;

  return (
    <div style={{ display:"flex", height:"calc(100vh - 160px)", gap:0, margin:"-20px", overflow:"hidden" }}>
      {showOrder && <OrderPanel patient={patient} onOrder={handleOrder} onClose={() => setShowOrder(false)} />}
      {showFavs  && <FavoritesPanel onSelect={(tests) => { setShowOrder(true); setShowFavs(false); }} onClose={() => setShowFavs(false)} />}

      {/* Left panel — order list */}
      <div style={{ width:"35%", borderRight:`1px solid ${C.border}`, display:"flex", flexDirection:"column", overflow:"hidden" }}>
        <div style={{ padding:"16px 16px 12px", borderBottom:`1px solid ${C.border}`, display:"flex", gap:8 }}>
          <button onClick={() => setShowOrder(true)}
            style={{ flex:1, padding:"8px", borderRadius:6, border:"none", background:C.teal, color:"#fff", cursor:"pointer", fontSize:12, fontWeight:700 }}>
            + Order Labs
          </button>
          <button onClick={() => setShowFavs(true)}
            style={{ padding:"8px 12px", borderRadius:6, border:`1px solid ${C.border2}`, background:"transparent", color:C.text2, cursor:"pointer", fontSize:12 }}>
            ★ Favs
          </button>
        </div>
        <div style={{ flex:1, overflowY:"auto" }}>
          {orders.length === 0 && (
            <div style={{ padding:"40px 20px", textAlign:"center", color:C.text3, fontSize:13 }}>No lab orders found.</div>
          )}
          {orders.map(o => (
            <div key={o.id} onClick={() => setSelected(o)}
              style={{
                padding:"12px 16px", cursor:"pointer", borderBottom:`1px solid ${C.border}`,
                background: selected?.id===o.id ? C.navy3 : "transparent",
                borderLeft: o.critical_values ? `3px solid #ff2222` : `3px solid transparent`,
                animation: o.critical_values ? "pulse-crit 2s infinite" : "none",
              }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
                <div style={{ fontWeight:600, fontSize:13, flex:1, paddingRight:8, color: o.critical_values ? "#ff4444" : C.text1 }}>
                  {o.panel_name}
                </div>
                <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                  <PriorityBadge priority={o.priority} />
                  <StatusBadge status={statusForDisplay(o)} />
                </div>
              </div>
              <div style={{ fontSize:11, color:C.text3 }}>{fmtDate(o.order_date)} · {o.facility}</div>
              {/* Action buttons */}
              <div style={{ display:"flex", gap:6, marginTop:6 }} onClick={e => e.stopPropagation()}>
                {o.status === "ordered" && (
                  <button onClick={e => { e.stopPropagation(); setDeleteLabConfirm(o); }}
                    style={{ fontSize:10, padding:"2px 8px", borderRadius:4, border:`1px solid ${C.red}40`, background:"transparent", color:C.red, cursor:"pointer" }}>
                    🗑 Delete
                  </button>
                )}
                {(o.status === "resulted" || o.status === "partial" || o.status === "final") && (
                  <button onClick={e => { e.stopPropagation(); setVoidModal(o); setVoidReason("Specimen labeling error"); setVoidNote(""); }}
                    style={{ fontSize:10, padding:"2px 8px", borderRadius:4, border:`1px solid ${C.amber}40`, background:"transparent", color:C.amber, cursor:"pointer" }}>
                    ⊘ Void
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delete Lab Confirmation Modal */}
      {deleteLabConfirm && createPortal(
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#162436", border:"1px solid rgba(255,255,255,0.12)", borderRadius:10, padding:24, width:"min(400px, 92vw)", boxShadow:"0 16px 48px rgba(0,0,0,0.6)" }}>
            <div style={{ fontWeight:600, fontSize:15, color:C.text1, marginBottom:8 }}>Delete this lab order?</div>
            <div style={{ fontSize:13, color:C.text2, marginBottom:6 }}><strong style={{ color:C.text1 }}>{deleteLabConfirm.panel_name}</strong></div>
            <div style={{ fontSize:12, color:C.red, marginBottom:20 }}>Only use for orders placed by mistake.</div>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button onClick={() => setDeleteLabConfirm(null)} style={{ padding:"7px 16px", borderRadius:6, border:`1px solid ${C.border2}`, background:"transparent", color:C.text2, cursor:"pointer", fontSize:12 }}>Cancel</button>
              <button onClick={handleDeleteLab} style={{ padding:"7px 16px", borderRadius:6, border:"none", background:C.red, color:"#fff", cursor:"pointer", fontSize:12, fontWeight:600 }}>Delete</button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Void Lab Modal */}
      {voidModal && createPortal(
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#162436", border:"1px solid rgba(255,255,255,0.12)", borderRadius:10, padding:24, width:"min(440px, 92vw)", boxShadow:"0 16px 48px rgba(0,0,0,0.6)" }}>
            <div style={{ fontWeight:600, fontSize:15, color:C.text1, marginBottom:8 }}>Void Lab Result</div>
            <div style={{ fontSize:13, color:C.text2, marginBottom:16 }}><strong style={{ color:C.text1 }}>{voidModal.panel_name}</strong></div>
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:11, color:C.text3, marginBottom:6 }}>Reason</div>
              <select value={voidReason} onChange={e => setVoidReason(e.target.value)}
                style={{ width:"100%", background:C.navy3, color:C.text1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"8px 10px", fontSize:12, outline:"none" }}>
                {["Specimen labeling error","Wrong patient","Duplicate order","Specimen not collected","Other"].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, color:C.text3, marginBottom:6 }}>Additional notes (optional)</div>
              <input value={voidNote} onChange={e => setVoidNote(e.target.value)} placeholder="Notes…"
                style={{ width:"100%", background:C.navy3, color:C.text1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"8px 10px", fontSize:12, outline:"none", boxSizing:"border-box" }} />
            </div>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button onClick={() => setVoidModal(null)} style={{ padding:"7px 16px", borderRadius:6, border:`1px solid ${C.border2}`, background:"transparent", color:C.text2, cursor:"pointer", fontSize:12 }}>Cancel</button>
              <button onClick={handleVoidLab} style={{ padding:"7px 16px", borderRadius:6, border:"none", background:C.amber, color:"#fff", cursor:"pointer", fontSize:12, fontWeight:600 }}>Void Result</button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Right panel — result viewer */}
      <div style={{ flex:1, padding:20, overflowY:"auto" }}>
        <ResultViewer order={selected} allOrders={orders} sex={patient.sex || "M"} />
      </div>
    </div>
  );
}

// ─── LABS PAGE (standalone) ───────────────────────────────────────────────────
const PAGE_TABS = ["All Results","Pending Orders","Critical Values"];

export default function LabsPage({ patients = [], onPatientSelect }) {
  const [orders, setOrders] = useState([]);
  const [tab, setTab]             = useState("All Results");
  const [selected, setSelected]   = useState(null);
  const [resultModal, setResultModal] = useState(null);
  const [acked, setAcked]         = useState(new Set());

  // Filters — All Results
  const [fStatus, setFStatus]   = useState("all");
  const [fCritOnly, setFCritOnly] = useState(false);
  const [fProvider, setFProvider] = useState("all");
  const [fSearch, setFSearch]   = useState("");
  const [resultsPage, setResultsPage] = useState(1);
  const debouncedLabSearch = useDebounce(fSearch);

  useEffect(() => { setResultsPage(1); }, [fStatus, fCritOnly, fProvider, debouncedLabSearch]);

  useEffect(() => {
    db.from("lab_orders").select("id, patient_id, order_date, panel_name, tests, priority, status, resulted_at, results, critical_values, ordered_by, practice_id").order("order_date", { ascending:false }).limit(100)
      .then(({ data }) => setOrders(data?.length ? data : (isDemoMode ? MOCK_LAB_ORDERS : [])));
  }, []);

  const patMap = useMemo(() => Object.fromEntries(patients.map(p=>[p.id,p])), [patients]);
  const providers = useMemo(() => ["all", ...new Set(orders.map(o=>o.ordered_by).filter(Boolean))], [orders]);

  const ptName = (pid) => { const p = patMap[pid]; return p ? `${p.first} ${p.last}` : pid; };
  const ptSex  = (pid) => patMap[pid]?.sex || "M";

  const handleSaveResult = (updated) => {
    setOrders(o => o.map(x => x.id === updated.id ? updated : x));
    setResultModal(null);
    if (updated.critical_values) setSelected(updated);
  };

  // Tab: All Results
  const allResulted = useMemo(() => {
    return orders.filter(o => {
      if (fStatus !== "all" && o.status !== fStatus) return false;
      if (fCritOnly && !o.critical_values) return false;
      if (fProvider !== "all" && o.ordered_by !== fProvider) return false;
      if (debouncedLabSearch) {
        const q = debouncedLabSearch.toLowerCase();
        if (!ptName(o.patient_id).toLowerCase().includes(q) && !o.panel_name.toLowerCase().includes(q)) return false;
      }
      return true;
    }).sort((a,b) => new Date(b.order_date) - new Date(a.order_date));
  }, [orders, fStatus, fCritOnly, fProvider, debouncedLabSearch]);

  const labsTotalPages = Math.ceil(allResulted.length / PAGE_SIZE);
  const pagedResults = allResulted.slice((resultsPage - 1) * PAGE_SIZE, resultsPage * PAGE_SIZE);

  // Tab: Pending Orders
  const pending = useMemo(() =>
    orders
      .filter(o => o.status === "ordered" || o.status === "pending")
      .sort((a,b) => {
        if (a.priority==="stat" && b.priority!=="stat") return -1;
        if (b.priority==="stat" && a.priority!=="stat") return 1;
        return new Date(a.order_date) - new Date(b.order_date);
      })
  , [orders]);

  // Tab: Critical Values
  const criticals = useMemo(() =>
    orders.filter(o => o.critical_values).sort((a,b) => new Date(b.resulted_at||0) - new Date(a.resulted_at||0))
  , [orders]);

  const waitColor = (days) => days >= 5 ? C.red : days >= 2 ? C.amber : C.text2;

  const Sel = ({ style, ...p }) => (
    <select style={{ background:C.navy3, color:C.text1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"6px 10px", fontSize:12, cursor:"pointer", outline:"none", ...style }} {...p} />
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", gap:0 }}>
      {/* Critical value banner inline */}
      {criticals.filter(o => !acked.has(o.id)).slice(0,1).map(o => (
        <div key={o.id} onClick={() => { setSelected(o); setTab("Critical Values"); }}
          style={{ background:"rgba(255,34,34,0.2)", border:`1px solid #ff3333`, borderRadius:8, padding:"10px 16px", marginBottom:16, display:"flex", alignItems:"center", gap:12, cursor:"pointer", animation:"pulse-crit 2s infinite" }}>
          <span style={{ fontSize:16 }}>🚨</span>
          <div style={{ flex:1 }}>
            <span style={{ fontWeight:700, color:"#ff4444" }}>CRITICAL VALUE</span>
            <span style={{ color:"#ff8888", marginLeft:10, fontSize:13 }}>{ptName(o.patient_id)} · {o.panel_name}</span>
            <span style={{ color:C.text2, fontSize:12, marginLeft:10 }}>
              {(Array.isArray(o.results) ? o.results : []).filter(r=>r.flag==="HH"||r.flag==="LL").map(r=>`${r.test}: ${r.value} ${r.units}`).join(" · ")}
            </span>
          </div>
          <button onClick={e=>{e.stopPropagation(); setAcked(s=>new Set([...s,o.id]));}}
            style={{ padding:"5px 12px", borderRadius:5, border:`1px solid #ff3333`, background:"transparent", color:"#ff6666", cursor:"pointer", fontSize:11, fontWeight:600, flexShrink:0 }}>
            Acknowledge
          </button>
        </div>
      ))}

      {/* Tab bar */}
      <div style={{ display:"flex", gap:2, borderBottom:`1px solid ${C.border}`, marginBottom:20 }}>
        {PAGE_TABS.map(t => {
          const badge = t==="Pending Orders" ? pending.length : t==="Critical Values" ? criticals.filter(o=>!acked.has(o.id)).length : null;
          return (
            <button key={t} onClick={() => setTab(t)} style={{
              padding:"10px 18px", border:"none", borderBottom:`2px solid ${tab===t ? C.teal : "transparent"}`,
              background:"transparent", color:tab===t ? C.teal : C.text2, fontWeight:tab===t ? 600 : 400,
              fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", gap:6,
            }}>
              {t}
              {badge > 0 && <span style={{ background: t==="Critical Values" ? "#ff2222" : C.amber, color: t==="Critical Values" ? "#fff" : C.navy, borderRadius:"50%", fontSize:10, fontWeight:700, minWidth:18, height:18, display:"inline-flex", alignItems:"center", justifyContent:"center", padding:"0 5px" }}>{badge}</span>}
            </button>
          );
        })}
      </div>

      {/* ── ALL RESULTS ── */}
      {tab==="All Results" && (
        <div style={{ display:"flex", gap:16, flex:1, overflow:"hidden" }}>
          <div style={{ flex:1, overflowY:"auto" }}>
            {/* Filters */}
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:14, alignItems:"center" }}>
              <input value={fSearch} onChange={e=>setFSearch(e.target.value)} placeholder="Search patient or panel…"
                style={{ background:C.navy3, color:C.text1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"6px 12px", fontSize:12, outline:"none", minWidth:200 }} />
              <Sel value={fStatus} onChange={e=>setFStatus(e.target.value)}>
                <option value="all">All statuses</option>
                <option value="resulted">Resulted</option>
                <option value="ordered">Ordered</option>
                <option value="pending">Pending</option>
              </Sel>
              <Sel value={fProvider} onChange={e=>setFProvider(e.target.value)}>
                {providers.map(p=><option key={p} value={p}>{p==="all" ? "All providers" : p}</option>)}
              </Sel>
              <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, cursor:"pointer" }}>
                <input type="checkbox" checked={fCritOnly} onChange={e=>setFCritOnly(e.target.checked)} style={{ accentColor:C.red }} />
                <span style={{ color:fCritOnly ? C.red : C.text2 }}>Critical only</span>
              </label>
              <div style={{ marginLeft:"auto", color:C.text2, fontSize:12 }}>{allResulted.length} results</div>
            </div>
            <div style={{ overflowX:"auto", borderRadius:8, border:`1px solid ${C.border}` }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr>
                    {["Patient","Panel","Order Date","Resulted","Status","Critical","Provider"].map(h=>(
                      <th key={h} style={{ padding:"9px 14px", textAlign:"left", fontSize:11, fontWeight:600, color:C.text2, textTransform:"uppercase", letterSpacing:"0.06em", background:C.navy3, borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allResulted.length===0 && <tr><td colSpan={7} style={{ padding:"40px", textAlign:"center", color:C.text3 }}>No records found.</td></tr>}
                  {pagedResults.map(o => {
                    const pt = patMap[o.patient_id];
                    return (
                      <tr key={o.id} onClick={() => setSelected(s => s?.id===o.id ? null : o)}
                        style={{ cursor:"pointer", background: selected?.id===o.id ? C.navy3 : "transparent", borderBottom:`1px solid ${C.border}`, borderLeft: o.critical_values ? `3px solid #ff2222` : `3px solid transparent` }}
                        onMouseEnter={e=>e.currentTarget.style.background=C.navy3}
                        onMouseLeave={e=>e.currentTarget.style.background=selected?.id===o.id?C.navy3:"transparent"}>
                        <td style={{ padding:"9px 14px" }}>
                          <button onClick={ev=>{ev.stopPropagation(); pt && onPatientSelect(pt,"labs");}}
                            style={{ background:"none", border:"none", color:C.teal, cursor:"pointer", fontSize:13, fontWeight:500, padding:0 }}>
                            {ptName(o.patient_id)}
                          </button>
                          {pt && <div style={{ fontSize:10, color:C.text3 }}>{pt.mrn}</div>}
                        </td>
                        <td style={{ padding:"9px 14px", fontWeight:500 }}>
                          {o.panel_name}
                          {o.priority==="stat" && <PriorityBadge priority="stat" />}
                        </td>
                        <td style={{ padding:"9px 14px", color:C.text2, whiteSpace:"nowrap" }}>{fmtDate(o.order_date)}</td>
                        <td style={{ padding:"9px 14px", color:C.text2, whiteSpace:"nowrap" }}>{fmtDate(o.resulted_at)}</td>
                        <td style={{ padding:"9px 14px" }}><StatusBadge status={o.critical_values?"critical":o.status} /></td>
                        <td style={{ padding:"9px 14px" }}>
                          {o.critical_values ? <span style={{ color:"#ff2222", fontWeight:700, fontSize:12 }}>🚨 YES</span> : <span style={{ color:C.text3, fontSize:12 }}>—</span>}
                        </td>
                        <td style={{ padding:"9px 14px", color:C.text2 }}>{o.ordered_by}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination currentPage={resultsPage} totalPages={labsTotalPages} onPageChange={setResultsPage} totalItems={allResulted.length} pageSize={PAGE_SIZE} />
          </div>
          {/* Slide-in detail */}
          {selected && (
            <div style={{ width:520, flexShrink:0, borderLeft:`1px solid ${C.border}`, paddingLeft:20, overflowY:"auto" }}>
              <ResultViewer order={selected} allOrders={orders.filter(o=>o.patient_id===selected.patient_id)} sex={ptSex(selected.patient_id)} onClose={()=>setSelected(null)} />
            </div>
          )}
        </div>
      )}

      {/* ── PENDING ORDERS ── */}
      {tab==="Pending Orders" && (
        <div style={{ overflowY:"auto" }}>
          {resultModal && (
            <MarkResultedModal order={resultModal} sex={ptSex(resultModal.patient_id)} onSave={handleSaveResult} onClose={()=>setResultModal(null)} />
          )}
          {pending.length === 0 && <div style={{ textAlign:"center", padding:"60px", color:C.text3 }}>No pending lab orders.</div>}
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {pending.map(o => {
              const days = daysSince(o.order_date);
              const isStatOrder = o.priority === "stat";
              return (
                <div key={o.id} style={{
                  background:C.navy2, border:`1px solid ${isStatOrder ? C.red : C.border}`,
                  borderRadius:10, padding:"14px 18px",
                  borderLeft: isStatOrder ? `4px solid ${C.red}` : `4px solid ${C.border}`,
                }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                        <span style={{ fontWeight:700, fontSize:14 }}>{o.panel_name}</span>
                        <PriorityBadge priority={o.priority} />
                        <StatusBadge status={o.status} />
                      </div>
                      <div style={{ fontSize:12, color:C.text2 }}>
                        <button onClick={() => { const pt=patMap[o.patient_id]; if(pt) onPatientSelect(pt,"labs"); }}
                          style={{ background:"none", border:"none", color:C.teal, cursor:"pointer", fontSize:12, fontWeight:500, padding:0, marginRight:6 }}>
                          {ptName(o.patient_id)}
                        </button>
                        · {o.facility} · {o.clinical_indication}
                      </div>
                      <div style={{ fontSize:11, color:C.text3, marginTop:4 }}>Tests: {(Array.isArray(o.tests) ? o.tests : []).join(", ")}</div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:16, flexShrink:0 }}>
                      <div style={{ textAlign:"center" }}>
                        <div style={{ fontSize:22, fontWeight:700, color:waitColor(days) }}>{days}</div>
                        <div style={{ fontSize:10, color:C.text3 }}>day{days!==1?"s":""} waiting</div>
                      </div>
                      <button onClick={() => setResultModal(o)}
                        style={{ padding:"8px 16px", borderRadius:6, border:"none", background:C.teal, color:"#fff", cursor:"pointer", fontSize:12, fontWeight:700 }}>
                        Mark Resulted
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── CRITICAL VALUES ── */}
      {tab==="Critical Values" && (
        <div style={{ overflowY:"auto" }}>
          {criticals.length === 0 && <div style={{ textAlign:"center", padding:"60px", color:C.text3 }}>No critical values on record.</div>}
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {criticals.map(o => {
              const isAcked = acked.has(o.id);
              const critResults = o.results?.filter(r=>r.flag==="HH"||r.flag==="LL") || [];
              const hoursAgo = o.resulted_at ? Math.floor((Date.now()-new Date(o.resulted_at).getTime())/3600000) : null;
              return (
                <div key={o.id} style={{
                  background: isAcked ? C.navy2 : "rgba(255,34,34,0.08)",
                  border:`2px solid ${isAcked ? C.border : "#ff2222"}`,
                  borderRadius:10, padding:"16px 20px",
                  animation: !isAcked ? "pulse-crit 2.5s infinite" : "none",
                  opacity: isAcked ? 0.6 : 1,
                }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
                        {!isAcked && <span style={{ fontSize:18 }}>🚨</span>}
                        <span style={{ fontWeight:700, fontSize:15, color: isAcked ? C.text2 : "#ff4444" }}>
                          {o.panel_name}
                        </span>
                        {isAcked && <span style={{ fontSize:11, color:C.green, fontWeight:600 }}>✓ Acknowledged</span>}
                      </div>
                      <div style={{ fontSize:12, color:C.text2, marginBottom:6 }}>
                        <button onClick={() => { const pt=patMap[o.patient_id]; if(pt) onPatientSelect(pt,"labs"); }}
                          style={{ background:"none", border:"none", color:C.teal, cursor:"pointer", fontSize:12, fontWeight:600, padding:0, marginRight:8 }}>
                          {ptName(o.patient_id)}
                        </button>
                        · {o.ordered_by} · {fmtDateTime(o.resulted_at)}
                        {hoursAgo !== null && <span style={{ marginLeft:8, color:hoursAgo < 2 ? C.red : C.amber }}>({hoursAgo}h ago)</span>}
                      </div>
                      {critResults.map((r,i) => (
                        <div key={i} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(255,34,34,0.15)", border:`1px solid #ff333360`, borderRadius:5, padding:"3px 10px", marginRight:6, marginBottom:4 }}>
                          <span style={{ fontSize:12, fontWeight:700, color:"#ff4444" }}>{r.test}: {r.value} {r.units}</span>
                          <FlagCell flag={r.flag} />
                          <span style={{ fontSize:10, color:"#ff8888" }}>ref: {r.refRange}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                      <button onClick={() => setSelected(s => s?.id===o.id ? null : o)}
                        style={{ padding:"8px 14px", borderRadius:6, border:`1px solid ${C.border2}`, background:"transparent", color:C.text2, cursor:"pointer", fontSize:12 }}>
                        View Full Results
                      </button>
                      {!isAcked && (
                        <button onClick={() => setAcked(s => new Set([...s, o.id]))}
                          style={{ padding:"8px 14px", borderRadius:6, border:"none", background:"#ff2222", color:"#fff", cursor:"pointer", fontSize:12, fontWeight:700 }}>
                          Acknowledge
                        </button>
                      )}
                    </div>
                  </div>
                  {selected?.id===o.id && (
                    <div style={{ marginTop:16, borderTop:`1px solid ${C.border}`, paddingTop:16 }}>
                      <ResultViewer order={o} allOrders={orders.filter(x=>x.patient_id===o.patient_id)} sex={ptSex(o.patient_id)} onClose={()=>setSelected(null)} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CRITICAL BANNER (EMRShell) ───────────────────────────────────────────────
export function LabCriticalBanner({ patients = [], onPatientSelect, ackedOrders, onAck }) {
  const [critOrders, setCritOrders] = useState(
    isDemoMode ? MOCK_LAB_ORDERS.filter(o => o.critical_values) : []
  );

  useEffect(() => {
    if (isDemoMode) return;
    db.from("lab_orders")
      .select("id, patient_id, order_date, panel_name, results, critical_values, ordered_by")
      .not("critical_values", "is", null)
      .order("order_date", { ascending: false })
      .limit(50)
      .then(({ data }) => setCritOrders(data || []));
  }, []);

  const unacked = critOrders.filter(o => o.critical_values && !ackedOrders.has(o.id));
  if (!unacked.length) return null;

  const first = unacked[0];
  const critResult = first.results?.find(r => r.flag==="HH"||r.flag==="LL");
  const pt = patients.find(p => p.id === first.patient_id);

  return (
    <div style={{
      background:"rgba(255,30,30,0.22)", borderBottom:"2px solid #ff2222",
      padding:"10px 24px", display:"flex", alignItems:"center", gap:14, cursor:"pointer",
      animation:"pulse-crit 1.8s infinite", zIndex:100,
    }} onClick={() => pt && onPatientSelect(pt, "labs")}>
      <span style={{ fontSize:20, flexShrink:0 }}>🚨</span>
      <div style={{ flex:1, lineHeight:1.3 }}>
        <span style={{ fontWeight:800, color:"#ff3333", fontSize:13, marginRight:10 }}>CRITICAL LAB VALUE</span>
        {pt && <span style={{ color:"#ff8888", fontSize:13, fontWeight:600, marginRight:10 }}>{pt.first} {pt.last}</span>}
        {critResult && <span style={{ color:"#ffaaaa", fontSize:12 }}>{critResult.test}: {critResult.value} {critResult.units}</span>}
        {unacked.length > 1 && <span style={{ color:C.text3, fontSize:11, marginLeft:10 }}>+{unacked.length-1} more</span>}
      </div>
      <span style={{ color:"#ff8888", fontSize:12, flexShrink:0 }}>Click to review →</span>
      <button onClick={e=>{e.stopPropagation(); onAck(first.id);}}
        style={{ padding:"5px 14px", borderRadius:5, border:"1px solid #ff4444", background:"transparent", color:"#ff6666", cursor:"pointer", fontSize:11, fontWeight:700, flexShrink:0 }}>
        Acknowledge
      </button>
    </div>
  );
}
